const messages = () => {
  try { return JSON.parse(document.getElementById('pf-validation-messages')?.textContent || '{}'); }
  catch { return {}; }
};
const states = new WeakMap();
const previewObservers = new WeakMap();
document.documentElement.setAttribute('data-pf-validation-ready', '');
let nextId = 0;
const hostFor = field => field.closest('[data-pf-colour-grid]') || field.closest('.pf-validation-field') || field;
function focusTarget(field) {
  const host = hostFor(field);
  return host.matches('[data-pf-colour-grid]') ? host.querySelector('[role="radio"][aria-checked="true"], [role="radio"]') || field : field;
}
export function clearFieldError(field) {
  previewObservers.get(field)?.disconnect();
  previewObservers.delete(field);
  const state = states.get(field);
  if (!state) return;
  state.message.remove();
  state.host.classList.remove('pf-field-invalid');
  field.removeAttribute('aria-invalid');
  state.host.removeAttribute('aria-invalid');
  const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== state.message.id);
  ids.length ? field.setAttribute('aria-describedby', ids.join(' ')) : field.removeAttribute('aria-describedby');
  if (state.focus !== field) {
    state.focus.removeAttribute('aria-invalid');
    const remaining = (state.focus.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== state.message.id);
    remaining.length ? state.focus.setAttribute('aria-describedby', remaining.join(' ')) : state.focus.removeAttribute('aria-describedby');
  }
  states.delete(field);
}
export function showFieldError(field, message) {
  if (!field || !message) return;
  clearFieldError(field);
  const host = hostFor(field), focus = focusTarget(field);
  const el = document.createElement('p');
  el.id = 'pf-field-error-' + (++nextId);
  el.className = 'pf-field-error';
  el.textContent = message;
  if (host === field) field.insertAdjacentElement('afterend', el);
  else host.append(el);
  host.classList.add('pf-field-invalid');
  host.setAttribute('aria-invalid', 'true');
  for (const node of new Set([field, focus])) {
    node.setAttribute('aria-invalid', 'true');
    node.setAttribute('aria-describedby', [node.getAttribute('aria-describedby'), el.id].filter(Boolean).join(' '));
  }
  states.set(field, { host, focus, message: el });
}
export function guideToField(field) {
  if (!field) return;
  const target = focusTarget(field), host = hostFor(field);
  const header = document.querySelector('#pf-menu, header-component, #header-component, .pf-header');
  const offset = Math.max(24, header?.getBoundingClientRect().bottom || 0) + 20;
  host.style.scrollMarginTop = offset + 'px';
  if (target.matches('pf-heart-preview')) target.tabIndex = -1;
  target.focus({ preventScroll: true });
  host.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}
function fieldMessage(field) {
  const t = messages();
  if (field.validity.typeMismatch && field.type === 'email') return t.email;
  if (field.validity.rangeOverflow || field.validity.rangeUnderflow || field.validity.stepMismatch || field.validity.badInput) return t.quantity;
  if (field.validity.tooLong) return t.too_long;
  if (field.matches('[data-pf-grid-select]')) return t.colour;
  if (field.type === 'file') return t.artwork;
  if (field.type === 'checkbox') return t.consent;
  if (/properties\[(?:Name|Initial)/.test(field.name)) return field.validity.valueMissing ? t.name_missing : t.name;
  if (field.type === 'email') return t.email;
  if (field.name === 'contact[body]') return t.message;
  return t.required;
}
export function validateFields(scope, { guide = true } = {}) {
  const invalid = [];
  for (const field of scope.querySelectorAll('input,select,textarea')) {
    if (!field.willValidate || field.disabled) continue;
    if (!field.validity.valid) {
      const artworkStatus = field.matches('[data-pf-file]') && field.closest('[data-pf-studio]')?.querySelector('[data-pf-artwork-status][data-state="error"]');
      if (!artworkStatus) showFieldError(field, fieldMessage(field));
      invalid.push(field);
    }
    else clearFieldError(field);
  }
  if (guide) guideToField(invalid[0]);
  return invalid;
}
export function validateProduct(form) {
  const invalid = validateFields(form, { guide: false });
  const t = messages();
  for (const studio of form.querySelectorAll('[data-pf-studio]')) {
    const button = studio.querySelector('[data-pf-open]');
    let saved = false;
    try { const value = JSON.parse(studio.querySelector('[data-pf-json]')?.value || '{}'); saved = value.format === 'print-finland-studio' && value.version === 1 && value.settings !== null && typeof value.settings === 'object'; } catch { /* No confirmed design yet. */ }
    if (!saved || !studio.querySelector('[data-pf-summary]')?.value || studio.dataset.unpriced === 'true') {
      showFieldError(button, studio.dataset.unpriced === 'true' ? t.design_price : t.design);
      if (button) invalid.push(button);
    } else clearFieldError(button);
    if (studio.dataset.mode === 'logo' && !studio.querySelector('[data-pf-upload]')?.files.length && studio.dataset.pfArtworkReady !== 'true') {
      const file = studio.querySelector('[data-pf-file]');
      if (file && !invalid.includes(file)) { showFieldError(file, t.artwork); invalid.push(file); }
    }
  }
  if (form.querySelector('[data-pf-heart-profile]')?.value === 'pf-heart-store-2026-10-28.1' &&
      (form.querySelector('pf-heart-preview')?.dataset.model !== 'ready' || !form.querySelector('[data-pf-heart-settings]')?.value)) {
    const preview = form.querySelector('pf-heart-preview');
    // The retry button is hidden while loading. Guide to the visible preview,
    // after any missing customer inputs, rather than focusing a hidden control.
    if (preview) {
      showFieldError(preview, t.preview_wait);
      const observer = new MutationObserver(() => {
        if (preview.dataset.model === 'ready' && form.querySelector('[data-pf-heart-settings]')?.value) {
          clearFieldError(preview); observer.disconnect();
        }
      });
      observer.observe(preview, { attributes: true, attributeFilter: ['data-model'] });
      previewObservers.set(preview, observer);
      setTimeout(() => observer.disconnect(), 120000);
      if (!invalid.length) invalid.push(preview);
    }
  } else {
    const preview = form.querySelector('pf-heart-preview');
    if (preview) clearFieldError(preview);
  }
  invalid.sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1);
  guideToField(invalid[0]);
  return invalid.length === 0;
}

// Capture native constraint failures too. Collect the whole form once, without
// calling checkValidity/reportValidity recursively or changing submission rules.
const queued = new WeakSet();
document.addEventListener('pf:validate-preview', event => {
  const scope = event.target.closest('.pf-keychain-piece') || event.target;
  if (validateFields(scope).length) event.preventDefault();
});
document.addEventListener('pf:artwork-error', event => guideToField(event.target));
document.addEventListener('invalid', event => {
  const field = event.target, form = field.form;
  if (!form || !(form.querySelector('[data-pf-keychain], [data-pf-studio]') || form.matches('form[action*="/contact"],form[action*="/cart"]'))) return;
  event.preventDefault();
  if (queued.has(form)) return;
  queued.add(form);
  queueMicrotask(() => { queued.delete(form); form.matches('[data-pf-product-form]') ? validateProduct(form) : validateFields(form); });
}, true);
for (const type of ['input', 'change']) document.addEventListener(type, event => {
  const field = event.target;
  if (field.willValidate && field.validity.valid) clearFieldError(field);
});
// Preview confirmation sets saved values programmatically. Observe only a
// currently flagged studio status; never scroll or focus while values change.
document.addEventListener('click', event => {
  if (!event.target.closest?.('[data-pf-open]')) return;
  const root = event.target.closest('[data-pf-studio]');
  const button = root?.querySelector('[data-pf-open]'), status = root?.querySelector('[data-pf-status]');
  if (!button || !status || !states.has(button)) return;
  const observer = new MutationObserver(() => {
    if (root.querySelector('[data-pf-json]')?.value && root.querySelector('[data-pf-summary]')?.value && root.dataset.unpriced !== 'true') {
      clearFieldError(button); observer.disconnect();
    }
  });
  observer.observe(status, { childList: true, subtree: true, characterData: true });
  setTimeout(() => observer.disconnect(), 120000);
});
